import gsap from "@lib/gsap";
import { onView, onDestroy } from "@/modules/_";
import { prepLineDraw, playLineDraw, cleanLineDraw } from "@utils/line-draw";

const TOGGLE_DURATION = 0.5;
const TOGGLE_EASE = "power2.inOut";

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  // --- SVG line draw ---
  const prepared = prepLineDraw(element);

  if (prepared) {
    let drawn = false;

    onView(element, {
      threshold: 0.15,
      autoStart: true,
      callback: ({ isIn }) => {
        if (isIn && !drawn) {
          drawn = true;
          playLineDraw(prepared);
        }
      },
    });
  }

  // --- Mobile nav toggle ---
  const headers = Array.from(
    element.querySelectorAll<HTMLElement>(".footer-nav_header, .div-block-2")
  );

  const pairs = headers.map((header) => {
    const list = header.nextElementSibling as HTMLElement | null;
    const arrow = header.querySelector<HTMLElement>(".footer-arrow");
    return { header, list, arrow };
  });

  const mq = window.matchMedia("(max-width: 991px)");
  let active = mq.matches;

  function closeAll() {
    for (const { list } of pairs) {
      if (!list) continue;
      gsap.set(list, { height: 0, overflow: "hidden", display: "block" });
      list.dataset.footerOpen = "false";
    }
  }

  function openAll() {
    for (const { list, arrow } of pairs) {
      if (!list) continue;
      gsap.set(list, { clearProps: "height,overflow,display" });
      if (arrow) gsap.set(arrow, { clearProps: "transform" });
      delete list.dataset.footerOpen;
    }
  }

  function toggle(index: number) {
    if (!active) return;

    const { list, arrow } = pairs[index];
    if (!list) return;

    const isOpen = list.dataset.footerOpen === "true";

    if (isOpen) {
      gsap.set(list, { overflow: "hidden" });
      gsap.to(list, {
        height: 0,
        duration: TOGGLE_DURATION,
        ease: TOGGLE_EASE,
      });
      if (arrow) gsap.to(arrow, { rotate: 90, duration: TOGGLE_DURATION, ease: TOGGLE_EASE });
      list.dataset.footerOpen = "false";
    } else {
      gsap.set(list, { display: "block" });
      gsap.to(list, {
        height: "auto",
        duration: TOGGLE_DURATION,
        ease: TOGGLE_EASE,
        onComplete: () => gsap.set(list, { overflow: "visible" }),
      });
      if (arrow) gsap.to(arrow, { rotate: -90, duration: TOGGLE_DURATION, ease: TOGGLE_EASE });
      list.dataset.footerOpen = "true";
    }
  }

  function onHeaderClick(this: HTMLElement) {
    const idx = headers.indexOf(this);
    if (idx !== -1) toggle(idx);
  }

  function handleBreakpoint() {
    active = mq.matches;
    if (active) {
      closeAll();
      headers.forEach((h) => {
        h.style.cursor = "pointer";
        h.addEventListener("click", onHeaderClick);
      });
    } else {
      openAll();
      headers.forEach((h) => {
        h.style.cursor = "";
        h.removeEventListener("click", onHeaderClick);
      });
    }
  }

  handleBreakpoint();
  mq.addEventListener("change", handleBreakpoint);

  // --- Cleanup ---
  onDestroy(() => {
    if (prepared) cleanLineDraw(prepared.path);
    mq.removeEventListener("change", handleBreakpoint);
    headers.forEach((h) => h.removeEventListener("click", onHeaderClick));
    openAll();
  });
}
